/* assets/cocktails.js
 *
 * Cocktails gallery: a justified-rows layout with a per-row fade-up on scroll,
 * plus the recipe detail panels.
 *
 * The layout is a port of the inline rows-gallery.js from the legacy Webflow
 * /cocktails page, preserving its algorithm and tuning so the migrated page
 * lays out identically:
 *
 *   - pack tiles into a row until the row would overflow the container
 *   - binary-search a row height in [minRowHeight, maxRowHeight] so the row's
 *     images, at their natural aspect ratios, exactly fill the container width
 *   - never upscale past an image's natural size
 *   - distribute rounding remainder so integer widths sum exactly to the row
 *   - absolutely position each tile; reveal each row on scroll
 *
 * Two deliberate departures from the original, both invisible in the result:
 *
 *   1. The original waited for all 67 images to decode before laying out,
 *      which blocked first paint and ruled out lazy loading. Natural
 *      dimensions are emitted server-side as data-w/data-h, so layout runs
 *      immediately and lazy loading is kept. Images without dimensions fall
 *      back to waiting for decode.
 *   2. Rows are re-laid out through a ResizeObserver on the container rather
 *      than a window resize listener, so a scrollbar appearing or the theme
 *      editor resizing the frame is handled too.
 *
 * Reference implementation for the layout maths: tools/simulate-gallery.py
 */
(function () {
  'use strict';

  var GALLERY = '[data-cocktail-gallery]';

  /* ------------------------------------------------------------- layout ---- */

  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  /* Floor each width, then hand the remainder to the largest fractions, so the
     integer widths sum to exactly `target` and the row has no sub-pixel seam. */
  function distributeWidths(floatWidths, target) {
    var intWidths = floatWidths.map(function (w) { return Math.floor(w); });
    var sumInt = intWidths.reduce(function (s, x) { return s + x; }, 0);
    var delta = Math.round(target - sumInt);
    var i;

    if (delta > 0) {
      var fracs = floatWidths
        .map(function (w, idx) { return { idx: idx, frac: w - Math.floor(w) }; })
        .sort(function (a, b) { return b.frac - a.frac; });
      for (i = 0; i < delta; i++) intWidths[fracs[i % fracs.length].idx]++;
    } else if (delta < 0) {
      var toRemove = -delta;
      var p = 0;
      while (toRemove > 0) {
        if (intWidths[p] > 1) { intWidths[p]--; toRemove--; }
        p = (p + 1) % intWidths.length;
      }
    }
    return intWidths;
  }

  function readOptions(container) {
    var d = container.dataset;
    return {
      gutter: parseInt(d.gutter, 10) || 10,
      minRowHeight: parseInt(d.minRowHeight, 10) || 440,
      maxRowHeight: parseInt(d.maxRowHeight, 10) || 650,
      preferredRowHeight: parseInt(d.preferredRowHeight, 10) || 550,
      mobileBreakpoint: parseInt(d.mobileBreakpoint, 10) || 768,
      maxPerRow: parseInt(d.maxPerRow, 10) || 6
    };
  }

  function measure(card) {
    var w = parseInt(card.getAttribute('data-w'), 10);
    var h = parseInt(card.getAttribute('data-h'), 10);
    if (w > 0 && h > 0) return { w: w, h: h };
    var img = card.querySelector('img');
    if (img && img.naturalWidth > 0) return { w: img.naturalWidth, h: img.naturalHeight };
    return null;
  }

  function layout(container) {
    var opts = readOptions(container);
    var containerWidth = container.clientWidth;
    // Zero width means an ancestor is still hidden (the age gate, a closed
    // theme-editor panel). Report "not ready" so the caller retries rather
    // than laying out into nothing.
    if (!containerWidth) return false;

    var cards = Array.prototype.slice.call(
      container.querySelectorAll('.bf-cocktail-card')
    );
    if (!cards.length) return false;

    var isMobile = containerWidth < opts.mobileBreakpoint;
    var minPerRow = isMobile ? 1 : 2;
    var maxPerRow = isMobile ? 1 : opts.maxPerRow;
    var targetHeight = clamp(opts.preferredRowHeight, opts.minRowHeight, opts.maxRowHeight);

    var items = [];
    for (var c = 0; c < cards.length; c++) {
      var size = measure(cards[c]);
      if (!size) return false;                    // dimensions not ready yet
      items.push({ card: cards[c], w: size.w, h: size.h, aspect: size.w / size.h });
    }

    var i = 0, y = 0, rowIndex = 0;
    var rows = [];

    while (i < items.length) {
      var row = [];
      var totalWidth = 0;
      var count = 0;

      while (i < items.length && count < maxPerRow) {
        var it = items[i];
        var heightUsed = Math.min(targetHeight, it.h);   // never upscale
        totalWidth += it.w * (heightUsed / it.h);
        row.push(it);
        count++;
        i++;
        if (totalWidth + count * opts.gutter >= containerWidth && count >= minPerRow) break;
      }

      var available = Math.max(0, containerWidth - opts.gutter * (row.length - 1));

      var widthsFor = function (H) {
        return row.map(function (r) { return r.aspect * Math.min(H, r.h); });
      };

      var lowH = opts.minRowHeight;
      var highH = opts.maxRowHeight;
      var bestH = lowH;
      for (var iter = 0; iter < 28; iter++) {
        var mid = (lowH + highH) / 2;
        var sum = widthsFor(mid).reduce(function (s, v) { return s + v; }, 0);
        if (sum > available) { highH = mid; } else { lowH = mid; }
        bestH = mid;
      }

      var finalHeight = Math.round(clamp(bestH, opts.minRowHeight, opts.maxRowHeight));
      var floatWidths = widthsFor(finalHeight).map(function (w, idx) {
        return Math.min(w, row[idx].w);                 // cap at natural width
      });
      var sumFloat = floatWidths.reduce(function (s, v) { return s + v; }, 0);
      var intWidths = sumFloat >= Math.max(1, available * 0.99)
        ? distributeWidths(floatWidths, available)
        : floatWidths.map(function (w) { return Math.round(w); });

      var cursor = 0;
      for (var k = 0; k < row.length; k++) {
        var el = row[k].card;
        var w = intWidths[k] || 0;
        el.style.position = 'absolute';
        el.style.left = cursor + 'px';
        el.style.top = y + 'px';
        el.style.width = w + 'px';
        el.style.height = finalHeight + 'px';
        el.setAttribute('data-rg-row', String(rowIndex));
        cursor += w + opts.gutter;
      }

      rows.push({ index: rowIndex, top: y, height: finalHeight });
      y += finalHeight + opts.gutter;
      rowIndex++;
    }

    container.style.height = Math.max(0, y - opts.gutter) + 'px';
    container.classList.add('bf-cocktails--laid-out');
    return rows;
  }

  /* ------------------------------------------------------------ reveal ---- */

  function observeRows(container, rows) {
    if (!rows || !rows.length) return null;
    var reduce = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || !('IntersectionObserver' in window)) {
      container.querySelectorAll('.bf-cocktail-card')
        .forEach(function (el) { el.classList.add('bf-cocktail-card--in'); });
      return null;
    }

    // Sentinels track each row's band, so a whole row reveals together the way
    // the original did, rather than tile by tile.
    container.querySelectorAll('.bf-rg-sentinel').forEach(function (s) { s.remove(); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var idx = entry.target.getAttribute('data-rg-sentinel');
        container
          .querySelectorAll('.bf-cocktail-card[data-rg-row="' + idx + '"]')
          .forEach(function (el) { el.classList.add('bf-cocktail-card--in'); });
        io.unobserve(entry.target);
      });
    }, { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    rows.forEach(function (row) {
      var s = document.createElement('div');
      s.className = 'bf-rg-sentinel';
      s.setAttribute('data-rg-sentinel', String(row.index));
      s.style.cssText = 'position:absolute;left:0;width:100%;pointer-events:none;'
        + 'top:' + row.top + 'px;height:' + row.height + 'px;';
      container.appendChild(s);

      // Reveal anything already in (or above) the viewport synchronously rather
      // than waiting for a callback. IntersectionObserver does not deliver
      // while a document is not being composited - a hidden tab, a background
      // window - which would otherwise leave the top of the gallery blank.
      var rect = s.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0 || rect.bottom <= 0) {
        container
          .querySelectorAll('.bf-cocktail-card[data-rg-row="' + row.index + '"]')
          .forEach(function (el) { el.classList.add('bf-cocktail-card--in'); });
        s.setAttribute('data-rg-revealed', '1');
        return;
      }
      io.observe(s);
    });

    return io;
  }

  /* ------------------------------------------------- touch reveal on scroll -- */
  /* On pointer-coarse viewports the original revealed titles on scroll-into-view
     rather than on hover (Webflow events e-29/e-30), and hid them again on the
     way out. Mirrored here. */
  function observeTouchTitles(container) {
    if (!('IntersectionObserver' in window)) return null;
    // Webflow ran this only on its medium/small/tiny breakpoints (<= 991px);
    // above that the reveal is hover-driven. Applying it everywhere would leave
    // desktop titles permanently visible.
    if (window.innerWidth > 991) return null;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target.classList.toggle('bf-cocktail-card--show', entry.isIntersecting);
      });
    }, { root: null, rootMargin: '0px 0px -20% 0px', threshold: 0.2 });
    container.querySelectorAll('.bf-cocktail-card')
      .forEach(function (el) { io.observe(el); });
    return io;
  }

  /* ------------------------------------------------------------- panels ---- */

  function initPanels(container) {
    var lastFocused = null;

    function openPanel() {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) return null;
      var panel;
      try { panel = document.querySelector(hash); } catch (e) { return null; }
      return panel && panel.classList.contains('bf-cocktail-panel') ? panel : null;
    }

    function sync() {
      var panel = openPanel();
      if (panel) {
        document.body.classList.add('bf-cocktail-open');
        var close = panel.querySelector('.bf-cocktail-panel__close');
        if (close && document.activeElement !== close) {
          window.requestAnimationFrame(function () { close.focus(); });
        }
      } else {
        document.body.classList.remove('bf-cocktail-open');
        if (lastFocused && document.contains(lastFocused)) {
          lastFocused.focus();
          lastFocused = null;
        }
      }
    }

    container.addEventListener('click', function (event) {
      var card = event.target.closest('.bf-cocktail-card');
      if (card) lastFocused = card;
    });

    window.addEventListener('hashchange', sync);

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var panel = openPanel();
      if (!panel) return;
      var close = panel.querySelector('.bf-cocktail-panel__close');
      if (close) close.click();
    });

    sync();
  }

  /* --------------------------------------------------------------- boot ---- */

  function boot(container) {
    var rowObserver = null;
    var touchObserver = null;

    function run() {
      var rows = layout(container);
      if (!rows) return false;                       // not ready; caller retries
      if (rowObserver) rowObserver.disconnect();
      rowObserver = observeRows(container, rows);
      if (touchObserver) { touchObserver.disconnect(); touchObserver = null; }
      if (window.innerWidth <= 991) {
        touchObserver = observeTouchTitles(container);
      } else {
        container.querySelectorAll('.bf-cocktail-card--show')
          .forEach(function (el) { el.classList.remove('bf-cocktail-card--show'); });
      }
      return true;
    }

    var pending;
    function relayout() {
      clearTimeout(pending);
      pending = setTimeout(run, 180);
    }

    // Install the width watcher before the first attempt, so a container that
    // is hidden at boot (age gate, collapsed editor panel) lays out as soon as
    // it gains a width.
    var lastWidth = container.clientWidth;
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        if (container.clientWidth === lastWidth) return;
        lastWidth = container.clientWidth;
        relayout();
      }).observe(container);
    } else {
      window.addEventListener('resize', relayout);
    }

    if (!run()) {
      // Either the container has no width yet, or an image lacks server-side
      // dimensions. Waiting on decode covers the second case; the
      // ResizeObserver above covers the first.
      var imgs = Array.prototype.slice.call(container.querySelectorAll('img'));
      Promise.all(imgs.map(function (img) {
        if (img.complete && img.naturalWidth) return Promise.resolve();
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      })).then(function () { run(); });
      // Last resort for a container revealed without a resize event firing.
      window.addEventListener('load', function () { if (!run()) relayout(); }, { once: true });
    }

    initPanels(container);
  }

  function init() {
    var container = document.querySelector(GALLERY);
    if (container) boot(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
